/**
 * Coach-roster rules for the Bookings screens: who is on your table, and
 * whether you may publish your own calendar.
 *
 * This module used to reproduce the backend's reporting graph client-side —
 * walking `EmployeeRoleManagers` links out of the whole-tenant `/fetch-employees`
 * payload, re-deriving depth, and re-checking every active window by hand.
 * `GET /fetch-employee-hierarchy` answers all of that server-side, so what is
 * left here is two much smaller jobs:
 *
 *   1. **Shaping the reporting tree into table rows** (`toCoachRows`), for a
 *      manager's own team. The endpoint returns every report regardless of
 *      department; the roster wants the Fitness/Nutritionist ones.
 *      **Presence in that response is no longer the edit right on its own** —
 *      the hierarchy still walks two depths, but `517da9b` cut
 *      `CallerScope.may_act_for` back to direct reports, so callers pass
 *      `directReportsOnly` and the two agree again. The hierarchy's own
 *      `_MAX_DEPTH = 2` is kept deliberately and the backend warns against
 *      "reconciling" the two numbers: seeing your org tree is not the same
 *      question as having authority over it.
 *
 *   1b. **Shaping the whole tenant into the same rows** (`toTenantCoachRows`),
 *      for an executive, out of `/fetch-employees`. Their authority is
 *      `is_executive` rather than a reporting line, so the tree cannot answer
 *      it — an executive who manages nobody still reaches every calendar.
 *
 *   2. **Describing the caller from context alone** — `isCoachManager`,
 *      `isCoachEmployee`, `describeCoachRole`, `holdsManagerTitle`. These read
 *      `/get-current-employee` (ApiContext `employeeData`), whose roles are
 *      `{ id, title }` with no department on them, hence `departmentsWithRoles`
 *      as a second argument wherever a department matters. They choose which
 *      *shape* of screen to paint and how to label it; they decide no authority.
 *
 * No React, no API — unit-testable in isolation.
 */

import { formatEmployeeList } from "../../Dashboard/EmpDashboard/employeeListUtils";
import { isExecutiveEmployee } from "../../../utils/accessControl";

/**
 * The departments that run call calendars.
 *
 * **No longer mirrors a backend predicate.** The manager side of the reporting
 * edge query used to require `LOWER(BTRIM(mgr_d.title)) IN ('fitness',
 * 'nutritionist')`; that filter was removed server-side, because it made the
 * depth-1 grant stricter than the depth-2 one. Authority is now a direct
 * reporting line, or executive standing, and nothing else.
 *
 * So this list is a *product* rule about what belongs on a bookings screen:
 * an HR or Marketing report is a legitimate report with no calendar, and
 * listing them here would be noise. Widening it widens the table, never access.
 */
export const COACH_MANAGER_DEPARTMENTS = ["Fitness", "Nutritionist"];

/**
 * Mirrors the backend's `has_manager_title` — an exact `'manager'` title match
 * with no department predicate, which is what now decides whether someone may
 * publish their *own* calendar. "Lead Coach" and "HR Specialist" do not qualify.
 */
export const MANAGER_ROLE_TITLES = ["Manager"];

/**
 * Coach role titles, per the backend's own seed data (`roles.csv`), where the
 * Fitness and Nutritionist departments each hold Manager, Lead Coach and Coach.
 *
 * Note this list gates only what the table *shows*, never what may be acted on:
 * the backend never inspects a reportee's role title, so the reporting line
 * grants authority whatever the coach's title is.
 */
export const COACH_ROLE_TITLES = ["Coach", "Lead Coach"];

/**
 * Fold a title for comparison: trimmed and lowercased.
 *
 * Same convention as `matchesAny` in `src/utils/accessControl.js`, deliberately
 * re-implemented rather than imported — that module pulls in the whole
 * feature-access graph, which would cost this one its "no React, no API" purity.
 *
 * @param {string|null|undefined} title
 * @returns {string} Empty string when there is no title.
 */
function normalizeTitle(title) {
  if (title == null) return "";
  return String(title).trim().toLowerCase();
}

/**
 * Exact match against a list of titles, case- and whitespace-insensitive.
 *
 * An empty or blank `value` never matches. This is the opposite of
 * `accessControl.matchesAny`, where an empty allow-list means "everyone passes"
 * — that rule is right for route config and wrong here, where failing to
 * identify someone's role must never widen access.
 *
 * @param {string|null|undefined} value
 * @param {Array<string>} allowed
 * @returns {boolean}
 */
function matchesTitle(value, allowed) {
  const normalized = normalizeTitle(value);
  if (!normalized) return false;
  return allowed.some((candidate) => normalizeTitle(candidate) === normalized);
}

/**
 * Whether a role title is a manager role.
 *
 * @param {string|null|undefined} roleTitle
 * @returns {boolean}
 */
export function isManagerRole(roleTitle) {
  return matchesTitle(roleTitle, MANAGER_ROLE_TITLES);
}

/**
 * Whether a role title is a coach role.
 *
 * @param {string|null|undefined} roleTitle
 * @returns {boolean}
 */
export function isCoachRole(roleTitle) {
  return matchesTitle(roleTitle, COACH_ROLE_TITLES);
}

/** Whether a department title is one where a Manager governs coach availability. */
function isCoachManagerDepartment(departmentTitle) {
  return matchesTitle(departmentTitle, COACH_MANAGER_DEPARTMENTS);
}

/**
 * A role entry arrives either wrapped (`/fetch-employees` returns
 * `{ role: {...}, managers: [...] }`) or flat (`/get-current-employee` returns
 * `{ id, title }`). Unwrap without caring which.
 */
function roleBody(entry) {
  return entry?.role || entry || {};
}

/** A role's title, tolerating both the `title` and `role_title` spellings. */
function roleTitleOf(entry) {
  const body = roleBody(entry);
  return body.title || body.role_title || "";
}

/** Roles.id → department title, from `/get-common-utils`'s departments_with_roles. */
function buildDepartmentTitleByRoleId(departmentsWithRoles) {
  const index = new Map();
  const departments = Array.isArray(departmentsWithRoles) ? departmentsWithRoles : [];
  departments.forEach((department) => {
    const title = department?.title || "";
    const roles = Array.isArray(department?.roles) ? department.roles : [];
    roles.forEach((role) => {
      if (role?.id != null) index.set(String(role.id), title);
    });
  });
  return index;
}

/** The roles array off `employeeData`, accepting the `{ data: ... }` envelope or a flat object. */
function currentEmployeeRoles(employeeData) {
  const data = employeeData?.data || employeeData;
  return Array.isArray(data?.roles) ? data.roles : [];
}

/**
 * The department this caller manages coaches in, or null if they manage none.
 *
 * Display-only — the roster itself is built from links, not from a department,
 * so this exists to title the screen ("Nutritionist coaches") rather than to
 * decide anything. A caller holding Manager in both coach departments resolves
 * to the first in payload order; the roster still spans every link they hold.
 *
 * No active-role filtering is applied because `/get-current-employee` already
 * restricts its roles to the currently active ones in SQL.
 *
 * @param {Object|null|undefined} employeeData - ApiContext `employeeData`.
 * @param {Array<Object>|null|undefined} departmentsWithRoles - From `/get-common-utils`.
 * @returns {string|null} The department title, or null.
 */
export function getManagedDepartmentTitle(employeeData, departmentsWithRoles) {
  return findCoachDepartmentTitle(employeeData, departmentsWithRoles, isManagerRole);
}

/**
 * A department's id, from its title — `departments_with_roles` is the only
 * place the frontend learns department ids, and a call-type ADD for a
 * department with no call types yet has no other row to read one from.
 *
 * @param {Array<Object>|null|undefined} departmentsWithRoles
 * @param {string} title
 * @returns {string|null}
 */
export function departmentIdByTitle(departmentsWithRoles, title) {
  const wanted = String(title || "").trim().toLowerCase();
  if (!wanted) return null;
  const match = (Array.isArray(departmentsWithRoles) ? departmentsWithRoles : []).find(
    (department) => String(department?.title || "").trim().toLowerCase() === wanted
  );
  return match?.id ?? null;
}

/**
 * Every coach department this caller holds a role in, matched by `matchesRoleTitle`.
 *
 * Shared by the manager and coach sides so they can only ever disagree about the
 * role title, never about which departments count or how a role is resolved to
 * one. The department test is what keeps both sides honest: role titles are
 * department-agnostic, so `Manager` alone could be an HR manager, and a future
 * `Coach` seeded outside Fitness/Nutritionist would otherwise be swept in.
 *
 * Returns them in payload order, deduped, so the singular helper below can take
 * the first and mean "the one to put in the header".
 */
function findCoachDepartmentTitles(employeeData, departmentsWithRoles, matchesRoleTitle) {
  const departmentByRoleId = buildDepartmentTitleByRoleId(departmentsWithRoles);
  const found = [];

  currentEmployeeRoles(employeeData).forEach((entry) => {
    if (!matchesRoleTitle(roleTitleOf(entry))) return;
    const departmentTitle = departmentByRoleId.get(String(roleBody(entry).id));
    if (!isCoachManagerDepartment(departmentTitle)) return;
    if (!found.includes(departmentTitle)) found.push(departmentTitle);
  });

  return found;
}

/** The first coach department this caller holds a matching role in, or null. */
function findCoachDepartmentTitle(employeeData, departmentsWithRoles, matchesRoleTitle) {
  return findCoachDepartmentTitles(employeeData, departmentsWithRoles, matchesRoleTitle)[0] ?? null;
}

/**
 * Which coach departments this caller's roster should cover.
 *
 * A Fitness Manager runs Fitness. Their reporting line may well reach further —
 * the backend puts no department test on it, so managing a Nutritionist Manager
 * genuinely grants their calendar — but a department's manager opening "My team"
 * expects their department, not everyone they could technically publish for.
 * This is the narrowing that produces that.
 *
 * **Empty means "no opinion", not "nobody".** Someone standing above the
 * departments — an executive, an HR director — holds no coach-department role
 * for this to read, and narrowing them to nothing would empty the one screen
 * they have. Callers treat `[]` as all coach departments.
 *
 * Matched on any coach-department role rather than a Manager title
 * specifically, so a Lead Coach who has people reporting to them is scoped the
 * same way their manager is.
 *
 * @param {Object|null|undefined} employeeData - ApiContext `employeeData`.
 * @param {Array<Object>|null|undefined} departmentsWithRoles - From `/get-common-utils`.
 * @returns {Array<string>} Department titles, possibly empty.
 */
export function getCoachDepartmentTitles(employeeData, departmentsWithRoles) {
  return findCoachDepartmentTitles(
    employeeData,
    departmentsWithRoles,
    (title) => isManagerRole(title) || isCoachRole(title)
  );
}

/**
 * Whether this caller is a coach whose availability a manager publishes for them.
 *
 * Restricted to the Fitness and Nutritionist departments on purpose, even though
 * `roles.csv` seeds Coach nowhere else today. Answering true is what makes this
 * person's own calendar read-only, and a coach in a department that has no
 * managers would then have nobody able to set their shifts at all.
 *
 * Note this chooses a screen, not a permission: whether a coach's own shifts
 * are writable is `holdsManagerTitle`, the backend's own-calendar rule.
 *
 * @param {Object|null|undefined} employeeData - ApiContext `employeeData`.
 * @param {Array<Object>|null|undefined} departmentsWithRoles - From `/get-common-utils`.
 * @returns {boolean}
 */
export function isCoachEmployee(employeeData, departmentsWithRoles) {
  return findCoachDepartmentTitle(employeeData, departmentsWithRoles, isCoachRole) !== null;
}

/**
 * How to describe this caller on the Bookings header — "Nutritionist manager",
 * "Fitness coach", "Nutritionist manager · also coaching", or any of those
 * behind an "Executive · " lead.
 *
 * The "also coaching" clause is not decoration. A manager gets an editable "My
 * shifts" tab whether or not they hold a coach role, because the Manager title
 * is what lets anyone set their own calendar — so when a
 * manager *is* also on the roster of people taking calls, this is the line that
 * says why their own availability sits next to their team's.
 *
 * Both departments are resolved independently, so someone holding Manager in
 * Nutritionist and Coach in Fitness is described by the department they manage;
 * the coach half only ever adds the suffix.
 *
 * **Executive standing leads when it is held**, because it is the widest of the
 * three and the one that explains the screen: it is why the table lists every
 * coach in the tenant rather than a reporting line. Until this was added the
 * standing vanished the moment its holder also held a coach-department role —
 * the header read plain "Nutritionist coach" while showing the whole company.
 *
 * Deliberately **not** phrased "Executive Coach": that is an established job
 * title meaning somebody who coaches executives, which in a product whose users
 * are all coaches reads as a specialty rather than as standing, and it would
 * drop the department that every other label here names.
 *
 * A **pure** executive — one holding no coach-department role, which is what
 * `rosterOnly` means — is simply "Executive". That is the whole line, and it is
 * the one case where standing is the description rather than a lead on one.
 * `ManagerBookings` used to supply "Publishes availability for every coach" here
 * on the reasoning that this function owned role titles and should not invent
 * one; that stopped holding the moment it learned the word "Executive", and
 * keeping the sentence in a second place would have meant two modules deciding
 * how to name the same standing.
 *
 * Note `isExecutiveEmployee` is a department titled `Executive`, not the
 * backend's `Employees.is_executive`; this is a label, so a disagreement
 * mislabels a header rather than granting anything.
 *
 * @param {Object|null|undefined} employeeData - ApiContext `employeeData`.
 * @param {Array<Object>|null|undefined} departmentsWithRoles - From `/get-common-utils`.
 * @returns {string|null} The description, or null for someone with neither a
 *   coach-department role nor executive standing.
 */
export function describeCoachRole(employeeData, departmentsWithRoles) {
  const managedDepartment = getManagedDepartmentTitle(employeeData, departmentsWithRoles);
  const coachDepartment = findCoachDepartmentTitle(
    employeeData,
    departmentsWithRoles,
    isCoachRole
  );

  let description = null;
  if (managedDepartment) {
    description = coachDepartment
      ? `${managedDepartment} manager · also coaching`
      : `${managedDepartment} manager`;
  } else if (coachDepartment) {
    description = `${coachDepartment} coach`;
  }

  const executive = isExecutiveEmployee(employeeData);
  if (!description) return executive ? "Executive" : null;
  return executive ? `Executive · ${description}` : description;
}

/**
 * Whether this caller is a coach manager — the cheap gate, from context alone.
 *
 * Reproduces `CallerScope.is_coach_manager`, which the backend now keeps as an
 * informational field that gates nothing. Here it still chooses a *screen
 * shape*: a departmental manager gets their own bookings and shifts alongside
 * their team, where someone standing above the departments gets the table alone.
 *
 * **It answers nothing about authority.** Someone it turns away may well reach
 * coaches — a manager's manager qualifies through any role in any department, so
 * they hold no role this could match. Who may be acted on comes from the
 * reporting tree (`toCoachRows`), never from here.
 *
 * @param {Object|null|undefined} employeeData
 * @param {Array<Object>|null|undefined} departmentsWithRoles
 * @returns {boolean}
 */
export function isCoachManager(employeeData, departmentsWithRoles) {
  return getManagedDepartmentTitle(employeeData, departmentsWithRoles) !== null;
}

/**
 * Whether this caller may publish their **own** availability.
 *
 * Mirrors `CallerScope.manager_title_employee_ids`, the rule added when writing
 * your own calendar stopped being automatic: `may_act_for` grants an own-calendar
 * write only to a caller holding a live role titled `Manager`. Publishing
 * availability is a manager's job here — a coach without that title does not
 * set their own shifts, whoever manages them does.
 *
 * Deliberately **not** department-scoped, unlike `isCoachManager`: the backend's
 * `has_manager_title` column is the same exact title match with no department
 * predicate, so an HR or Operations Manager passes this and a Fitness Lead Coach
 * does not.
 *
 * No active-window filtering is applied because `/get-current-employee` already
 * restricts its roles to the currently active ones in SQL.
 *
 * @param {Object|null|undefined} employeeData - ApiContext `employeeData`.
 * @returns {boolean}
 */
export function holdsManagerTitle(employeeData) {
  return currentEmployeeRoles(employeeData).some((entry) =>
    isManagerRole(roleTitleOf(entry))
  );
}

/** A hierarchy report's `roles` array. */
function reportRoles(report) {
  return Array.isArray(report?.roles) ? report.roles : [];
}

/** A person's display name from a hierarchy report, or an em dash. */
function reportName(report) {
  const name = `${report?.first_name || ""} ${report?.last_name || ""}`.trim();
  return name || "—";
}

/**
 * Turn `/fetch-employee-hierarchy`'s flat `reports[]` into coach-roster rows.
 *
 * **Every row this produces is one the caller may act on** — given
 * `directReportsOnly`, which every caller now passes. The endpoint walks the
 * same live `EmployeeRoleManagers` edges with the same absence of any title or
 * department test that `CallerScope.may_act_for` applies to somebody else's
 * calendar; it just walks one hop further than `may_act_for` grants, which is
 * what that flag trims back. So membership *is* the grant and there is no
 * `canEdit` to carry. A coach with no manager link simply is not in the
 * response, which is why the table cannot show them at all.
 *
 * What this does that the server does not — all of it narrowing, never widening:
 *
 * - **Filters to `departments`.** The hierarchy covers the whole org, so an HR or
 *   Marketing report comes back too; they have no call calendar and are dropped
 *   rather than rendered as a row that opens onto nothing. The caller's own
 *   department narrows it further — see `getCoachDepartmentTitles`.
 * - **Filters to `roleTitles`.** A departmental manager's team is coaches; another
 *   Manager who reports to them is a peer running their own team, not a row on
 *   theirs.
 * - **Filters to direct reports** when `directReportsOnly` is set, which is
 *   also what keeps this in step with the backend: `may_act_for` grants a
 *   direct reporting line only, so a depth-2 row would be one that 403s on the
 *   first attempt to publish for it. It leaves the table one legible
 *   invariant — every row is one this caller manages themselves — which is why
 *   `CoachRosterTab` drops the Manager column here and keeps it only on the
 *   executive's tenant-wide table.
 * - **Resolves `manager_employee_id` to a name.** Always possible without a
 *   second lookup: a level-2 row's manager is a level-1 row in the same
 *   response, and a level-1 row's manager is the caller.
 *
 * Nothing here re-checks liveness. The server has already applied
 * `holds_role_now` to both ends of every edge, filtered `roles[]` to the
 * currently active ones, excluded the caller, and returned one row per person at
 * their minimum depth.
 *
 * @param {Object} params
 * @param {Array<Object>|null|undefined} params.reports - `data.reports` as returned.
 * @param {string|null|undefined} params.callerEmployeeId - The caller's Employees id.
 * @param {Array<string>} [params.departments] - Department titles to keep.
 *   Empty or omitted means every coach department.
 * @param {Array<string>} [params.roleTitles] - Role titles to keep. Empty or
 *   omitted means every title that holds a calendar, managers included.
 * @param {boolean} [params.directReportsOnly=false] - Keep only people assigned
 *   to the caller themselves, dropping anyone reached through someone else.
 * @returns {Array<{
 *   employeeId: string,
 *   userId: string|null,
 *   name: string,
 *   email: string,
 *   roleTitle: string,
 *   departmentTitle: string,
 *   managerName: string,
 *   depth: 1|2|null,
 *   slotsThisWeek: number,
 *   callsToday: number,
 * }>}
 */
export function toCoachRows({
  reports,
  callerEmployeeId,
  departments,
  roleTitles,
  directReportsOnly = false,
}) {
  const list = Array.isArray(reports) ? reports : [];
  if (list.length === 0) return [];

  // An empty scope is "show every coach department", never "show none" — a
  // blank table is the one outcome that would look like a bug rather than a
  // rule, and it is what an executive would get. Same reading for the titles.
  const allowedDepartments =
    Array.isArray(departments) && departments.length > 0
      ? departments
      : COACH_MANAGER_DEPARTMENTS;
  const allowedTitles =
    Array.isArray(roleTitles) && roleTitles.length > 0
      ? roleTitles
      : [...COACH_ROLE_TITLES, ...MANAGER_ROLE_TITLES];

  const callerId = callerEmployeeId == null ? null : String(callerEmployeeId);

  // Built over *every* report, not just the ones that survive the department
  // filter: a coach's manager is named here even when that manager holds no
  // coach-department role themselves.
  const nameByEmployeeId = new Map();
  list.forEach((report) => {
    const id = report?.employee_id == null ? null : String(report.employee_id);
    if (id) nameByEmployeeId.set(id, reportName(report));
  });
  // "You" rather than the caller's own name: in a table the caller is looking
  // at, repeating their name in a column about somebody else's manager reads as
  // a third party.
  if (callerId) nameByEmployeeId.set(callerId, "You");

  const rows = [];

  list.forEach((report) => {
    const employeeId = report?.employee_id == null ? null : String(report.employee_id);
    if (!employeeId) return;
    // The server excludes the caller (`WHERE h.depth > 0`); this is the cheap
    // guard against a future where it does not, since a table of other people's
    // calendars listing your own would be wrong.
    if (callerId && employeeId === callerId) return;

    const managerId =
      report?.manager_employee_id == null ? null : String(report.manager_employee_id);

    // "Assigned to me" has to be positively established, so a report with no
    // resolvable manager is dropped rather than assumed to be ours. Equivalent
    // to `hierarchy_level === 1`, but stated against the field the Manager
    // column renders, so the table and the filter cannot disagree.
    if (directReportsOnly && (!callerId || managerId !== callerId)) return;

    // First role in the response's order (it sorts them by title) that is both a
    // calendar-holding title and in an allowed department. `department_title`
    // comes on the role itself, so no `departmentsWithRoles` lookup is needed.
    const role = reportRoles(report).find(
      (entry) =>
        matchesTitle(entry?.title, allowedTitles) &&
        matchesTitle(entry?.department_title, allowedDepartments)
    );
    if (!role) return;

    rows.push({
      employeeId,
      userId: report?.user_id || null,
      name: reportName(report),
      email: report?.email || "",
      roleTitle: role.title || "—",
      departmentTitle: role.department_title || "—",
      managerName: (managerId && nameByEmployeeId.get(managerId)) || "—",
      depth: report?.hierarchy_level ?? null,
      slotsThisWeek: 0,
      callsToday: 0,
    });
  });

  return rows;
}

/**
 * Every Fitness and Nutritionist coach in the tenant — the executive's table.
 *
 * A different question from `toCoachRows`, answered from a different endpoint.
 * `/fetch-employee-hierarchy` returns the caller's *reporting tree*, so an
 * executive who manages nobody gets an empty array from it — which is the
 * whole problem: their authority does not come from a reporting line at all.
 * `may_act_for` checks `is_executive` first and grants every calendar, so the
 * table has to be sourced from the tenant list to match.
 *
 * `/fetch-employees` is that list, and it needs no special standing: it applies
 * no ownership or role gate, so restricting this view to executives is the
 * frontend choosing what to paint, not a permission it enforces. The write is
 * authorized server-side either way.
 *
 * Reuses `formatEmployeeList` from the Employees page rather than re-shaping
 * the payload here. That is a cross-page util import, which this repo does not
 * otherwise do, and it is worth it: that function already dedupes a person who
 * appears more than once, resolves each role's department through
 * `departmentsWithRoles`, and — the part that matters — filters roles through
 * `isRoleCurrent`, the mirror of the backend's `holds_role_now`. Unlike
 * `/fetch-employee-hierarchy`, `/fetch-employees` applies **no active-window
 * filter to roles**, so a lapsed Coach assignment comes back looking live.
 * Re-deriving that rule here would be a second copy of it.
 *
 * Rows come back in the same shape `toCoachRows` produces, so `CoachRosterTab`
 * cannot tell which endpoint fed it.
 *
 * @param {Object} params
 * @param {Array<Object>|null|undefined} params.employees - `data.employees` from
 *   `/fetch-employees`.
 * @param {Array<Object>|null|undefined} params.departmentsWithRoles - From
 *   `/get-common-utils`; how a role id resolves to a department title.
 * @param {string|null|undefined} [params.callerEmployeeId] - Dropped from the
 *   table if present on it, the same way `toCoachRows` drops it: a table of
 *   other people's calendars listing your own would be wrong.
 * @param {Array<string>} [params.departments] - Department titles to keep.
 * @param {Array<string>} [params.roleTitles] - Role titles to keep. Defaults to
 *   every title that holds a calendar in a coach department, **managers
 *   included**: a Fitness Manager takes calls as well as publishing for their
 *   coaches, and an executive may publish for them, so leaving them off would
 *   put a calendar on nobody's table.
 * @returns {Array<Object>} Same row shape as `toCoachRows`, with `depth` null.
 */
export function toTenantCoachRows({
  employees,
  departmentsWithRoles,
  callerEmployeeId,
  departments = COACH_MANAGER_DEPARTMENTS,
  roleTitles = [...COACH_ROLE_TITLES, ...MANAGER_ROLE_TITLES],
}) {
  const callerId = callerEmployeeId == null ? null : String(callerEmployeeId);
  // Resolved here rather than trusting `formatEmployeeList`'s own department
  // resolution, which falls back to `department.name` — a key
  // `departments_with_roles` does not have, since its entries carry `title`.
  // Its earlier branches do work when the role carries a department id, so
  // this prefers the role-id map and keeps that answer as the fallback.
  const departmentTitleByRoleId = buildDepartmentTitleByRoleId(departmentsWithRoles);
  const departmentTitleOf = (entry) =>
    departmentTitleByRoleId.get(String(entry?.role_id)) || entry?.department_title || "";
  const rows = [];

  formatEmployeeList(employees, departmentsWithRoles).forEach((employee) => {
    // `formatEmployeeList` already nulls an `employee_id` that is really a
    // user_id — a TOS-signed person with no Employees row, whom this endpoint
    // includes and who has no calendar to open.
    const employeeId = employee?.employee_id == null ? null : String(employee.employee_id);
    if (!employeeId) return;
    if (callerId && employeeId === callerId) return;

    const role = (employee.current_roles || []).find(
      (entry) =>
        matchesTitle(entry?.role_title, roleTitles) &&
        matchesTitle(departmentTitleOf(entry), departments)
    );
    if (!role) return;

    rows.push({
      employeeId,
      userId: employee.user_id || null,
      name: employee.name || "Unknown",
      email: employee.email || "",
      roleTitle: role.role_title || "—",
      departmentTitle: departmentTitleOf(role) || "—",
      // Who they report to, which `/fetch-employees` carries on the role. Worth
      // keeping here where `toCoachRows` no longer needs it: on a table of the
      // whole tenant it is the column that says which manager's team a coach is
      // on, rather than restating the caller's own name on every row.
      managerName: role.manager_name && role.manager_name !== "N/A" ? role.manager_name : "—",
      // The reporting tree is not what put this row here, so there is no depth
      // to report. Kept on the shape so the two builders stay interchangeable.
      depth: null,
      slotsThisWeek: 0,
      callsToday: 0,
    });
  });

  return rows.sort((a, b) => a.name.localeCompare(b.name));
}
